import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { applyPwaManifest } from '../utils/pwa';
import PwaInstallPanel from '../components/common/PwaInstallPanel';
import { parseDateInputValue } from '../utils/helpers';
import { useApp } from '../context/AppContext';
import { createQuotation } from '../services/firestoreService';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Button from '../components/common/Button';
import SignaturePad from '../components/common/SignaturePad';
import GuristaGuryahaTable from '../components/quotation/GuristaGuryahaTable';
import GuristaXafiisyadaTable from '../components/quotation/GuristaXafiisyadaTable';
import GuristaGanacsigadaTable from '../components/quotation/GuristaGanacsigadaTable';
import { SERVICE_TYPES, PAYMENT_TYPES, HOUSE_TYPES, FLOOR_OPTIONS, BIYAANO_FEATURES, DESTINATION_TYPES } from '../utils/constants';
import toast from 'react-hot-toast';
import { 
  FiHome, 
  FiFileText, 
  FiPhone, 
  FiInfo, 
  FiTruck, 
  FiMapPin, 
  FiClock, 
  FiMail, 
  FiSend, 
  FiStar, 
  FiCheck, 
  FiArrowRight, 
  FiPlay, 
  FiShield, 
  FiAward, 
  FiUsers,
  FiChevronUp,
  FiCopy
} from 'react-icons/fi';

// Payment provider logo mapping
const PAYMENT_LOGOS = {
  'EVC Plus': '/evc.png',
  'Dahabshiil Bank': '/dahabshiil.png',
  'Salaam Bank': '/salam.png',
  'Primmer Bank': '/primer.png',
  'IBS Bank': '/ibs.png',
};

const PublicQuotation = () => {
  const { paymentAccounts, loadingPayments } = useApp();
  const [searchParams] = useSearchParams();
  const showInstallPrompt = useMemo(
    () => searchParams.get('install') === '1' || searchParams.get('source') === 'pwa',
    [searchParams]
  );

  const [formData, setFormData] = useState({
    customerName: '',
    phone: '',
    fromLocation: '',
    toLocation: '',
    movingDate: '',
    serviceType: '',
    paymentType: '',
    paymentAccountId: '',
    houseType: '',
    floorLevel: '',
    biyaanoFeatures: [],
    destinationType: '',
    destinationFloor: '',
    dabaqNameFrom: '',
    dabaqNameTo: '',
    items: [],
    customerSignature: ''
  });
  
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedRef, setSubmittedRef] = useState('');
  const [activeNav, setActiveNav] = useState('section-home');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [copied, setCopied] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    applyPwaManifest('client');
  }, []);

  /// ✅ SIMPLE WORKING SCROLL FUNCTION
const scrollToSection = (sectionId) => {
  setActiveNav(sectionId);
  
  const element = document.getElementById(sectionId);
  
  if (element) {
    element.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  } else if (sectionId === 'section-home') {
    // Fallback for home if for some reason the element isn't found
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};
  // Scroll detection for active nav and scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;
      
      setShowScrollTop(window.scrollY > 500);

      const sections = ['section-home', 'section-services', 'section-quotation', 'section-about', 'section-contact'];
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const offsetTop = section.offsetTop;
          if (scrollPosition >= offsetTop) {
            setActiveNav(sections[i]);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Copy account number to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Lambarka waa la koobiyey!');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error('Copy ma shaqeynin');
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'serviceType') {
        next.items = [];
      }

      if (name === 'paymentType') {
        const account = paymentAccounts.find((acc) => acc.paymentType === value);
        next.paymentAccountId = account?.id || '';
      }

      if (name === 'houseType') {
        next.floorLevel = '';
        next.biyaanoFeatures = [];
        next.destinationType = '';
        next.destinationFloor = '';
        next.dabaqNameFrom = '';
        next.dabaqNameTo = '';
      }

      if (name === 'floorLevel') {
        next.destinationType = '';
        next.destinationFloor = '';
        next.dabaqNameTo = '';
      }

      if (name === 'destinationType' && value !== 'dabaq') {
        next.destinationFloor = '';
        next.dabaqNameTo = '';
        next.biyaanoFeatures = [];
      }

      return next;
    });
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFeatureChange = (featureValue) => {
    setFormData(prev => {
      const currentFeatures = prev.biyaanoFeatures || [];
      if (currentFeatures.includes(featureValue)) {
        return {
          ...prev,
          biyaanoFeatures: currentFeatures.filter(f => f !== featureValue)
        };
      } else {
        return {
          ...prev,
          biyaanoFeatures: [...currentFeatures, featureValue]
        };
      }
    });
  };

  const handleItemsChange = (items) => {
    setFormData(prev => ({ ...prev, items }));
  };

  const handleSignatureSave = (signature) => {
    setFormData(prev => ({ ...prev, customerSignature: signature }));
    if (errors.customerSignature) {
      setErrors(prev => ({ ...prev, customerSignature: '' }));
    }
  };

  const handleSignatureClear = () => {
    setFormData(prev => ({ ...prev, customerSignature: '' }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.customerName.trim()) newErrors.customerName = 'Magaca waa lagama maarmaan';
    if (!formData.phone.trim()) newErrors.phone = 'Telefoonka waa lagama maarmaan';
    if (!formData.fromLocation.trim()) newErrors.fromLocation = 'Goobta waa lagama maarmaan';
    if (!formData.toLocation.trim()) newErrors.toLocation = 'Goobta waa lagama maarmaan';
    if (!formData.movingDate) newErrors.movingDate = 'Taariikhda waa lagama maarmaan';
    if (!formData.serviceType) newErrors.serviceType = 'Adeegga waa lagama maarmaan';
    if (!formData.paymentType) newErrors.paymentType = 'Nooca lacagta waa lagama maarmaan';
    if (!formData.houseType) newErrors.houseType = 'Nooca guriga waa lagama maarmaan';
    if (formData.houseType === 'dabaq' && !formData.floorLevel) {
      newErrors.floorLevel = 'Biyaanada waa lagama maarmaan';
    }
    if (formData.houseType === 'dabaq' && !formData.dabaqNameFrom?.trim()) {
      newErrors.dabaqNameFrom = 'Magaca dabaqka / biyaaanka waa lagama maarmaan';
    }
    if (formData.houseType === 'dabaq' && formData.floorLevel && !formData.destinationType) {
      newErrors.destinationType = 'Xaggee ayaad u gurayseysaa waa lagama maarmaan';
    }
    if (formData.houseType === 'guri-caadi' && !formData.destinationType) {
      newErrors.destinationType = 'Xaggee ayaad u gurayseysaa waa lagama maarmaan';
    }
    if (formData.destinationType === 'dabaq' && !formData.destinationFloor) {
      newErrors.destinationFloor = 'Biyaanada aad u gurayseysaa waa lagama maarmaan';
    }
    if (formData.destinationType === 'dabaq' && !formData.dabaqNameTo?.trim()) {
      newErrors.dabaqNameTo = 'Magaca dabaqka / biyaaanka (u gurayso) waa lagama maarmaan';
    }
    if (!formData.customerSignature) newErrors.customerSignature = 'Saxiixa waa lagama maarmaan';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Fadlan buuxi dhammaan meelaha');
      return;
    }

    setSubmitting(true);

    const result = await createQuotation({
      ...formData,
      movingDate: parseDateInputValue(formData.movingDate)
    });

    if (result.success) {
      toast.success('Codsigaaga waa la diray!');
      setSubmittedRef(result.quotationNumber || '');
      setSubmitted(true);
    } else {
      toast.error('Khalad: ' + result.error);
    }

    setSubmitting(false);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    toast.success('Farriintaada waa la diray! Waan kula soo xiriiri doonaa.');
    setContactForm({ name: '', email: '', phone: '', message: '' });
  };

  const selectedPaymentAccount = paymentAccounts.find(
    acc => acc.paymentType === formData.paymentType
  );

  // Navigation items
  const navItems = [
    { id: 'section-home', label: 'Home', icon: FiHome },
    { id: 'section-services', label: 'Adeegyada', icon: FiTruck },
    { id: 'section-quotation', label: 'Quotation', icon: FiFileText },
    { id: 'section-about', label: 'About', icon: FiInfo },
    { id: 'section-contact', label: 'Contact', icon: FiPhone },
  ];

  // Services data
  const services = [
    {
      icon: FiHome,
      title: 'Guurista Guryaha',
      description: 'Adeeg buuxa oo ku saabsan guuritaanka guryaha qoyska',
      features: ['Alaabta guriga oo dhan', 'Shaqaale xirfad leh', 'Gaari ku habboon'],
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: FiFileText,
      title: 'Guurista Xafiisyada',
      description: 'Adeeg gaar ah oo loo diyaariyey xafiisyada iyo shirkadaha',
      features: ['Qalabka xafiiska', 'Kombuyuutarada', 'Dokumentiyada'],
      color: 'from-green-500 to-green-600'
    },
    {
      icon: FiTruck,
      title: 'Guurista Ganacsiyada',
      description: 'Adeeg weyn oo loogu talagalay ganacsiyada waaweyn',
      features: ['Alaab badan', 'Gaariyo waaweyn', '24/7 Support'],
      color: 'from-orange-500 to-orange-600'
    }
  ];

  // Stats data
  const stats = [
    { number: '70+', label: 'Macmiil', icon: FiUsers },
    { number: '100+', label: 'Guurin', icon: FiTruck },
    { number: '3+', label: 'Sanno', icon: FiAward },
    { number: '24/7', label: 'Support', icon: FiClock }
  ];

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center border border-gray-100">
          <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-bounce">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Mahadsanid!</h2>
          <p className="text-gray-600 mb-4 text-lg">
            Codsigaaga waa la helay. Waxaan kula soo xiriiri doonaa dhawaan.
          </p>
          {submittedRef ? (
            <p className="text-gray-800 mb-8">
              Lambarka tixraaca:{' '}
              <span className="font-mono font-bold text-primary-600">{submittedRef}</span>
            </p>
          ) : (
            <div className="mb-8" />
          )}
          <Button onClick={() => window.location.reload()} fullWidth size="lg">
            Codsi Cusub
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      
      {/* ============ DESKTOP NAVIGATION ============ */}
      <nav className="hidden lg:block bg-white/95 backdrop-blur-lg shadow-lg sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div 
              className="flex items-center space-x-3 cursor-pointer" 
              onClick={() => scrollToSection('section-home')}
            >
              <img src="/logo.png" alt="Sahalguur Logo" className="h-14 w-auto" />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-red-700 leading-tight">SAHALGUUR</span>
                <span className="text-xs font-medium text-blue-600 tracking-wider">SERVICES COMPANY</span>
              </div>
            </div>

            {/* Nav Links */}
            <div className="flex items-center space-x-1">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                    className={`px-5 py-2.5 rounded-full font-medium transition-all duration-300 flex items-center space-x-2
                      ${activeNav === item.id 
                        ? 'bg-red-600 text-white shadow-lg shadow-red-200' 
                        : 'text-gray-700 hover:bg-red-50 hover:text-red-600'
                      }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Contact Badge */}
            <a href="tel:+252614262600" className="flex items-center space-x-4">
              <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-5 py-2.5 rounded-full flex items-center space-x-2 shadow-lg hover:shadow-xl hover:scale-105 transition-all">
                <FiPhone className="w-4 h-4 animate-pulse" />
                <span className="font-bold">+25261 4262600</span>
              </div>
            </a>
          </div>
        </div>
      </nav>

      {showInstallPrompt && (
        <div className="sticky top-0 z-[60] bg-red-600 text-white px-4 py-3 shadow-lg">
          <PwaInstallPanel mode="client" layout="compact" className="!text-white [&_p]:!text-red-50" />
        </div>
      )}

      {/* ============ MOBILE HEADER ============ */}
      <header className="lg:hidden bg-white/95 backdrop-blur-lg shadow-md sticky top-0 z-50">
        <div className="px-4 py-3 flex items-center justify-between">
          <div 
            className="flex items-center space-x-2 cursor-pointer" 
            onClick={() => scrollToSection('section-home')}
          >
            <img src="/logo.png" alt="Logo" className="h-10 w-auto" />
            <div className="flex flex-col">
              <span className="text-sm font-bold text-red-700 leading-tight">SAHALGUUR</span>
              <span className="text-[10px] font-medium text-blue-600">SERVICES COMPANY</span>
            </div>
          </div>
          <a href="tel:+252614262600" className="bg-gradient-to-r from-red-600 to-red-700 text-white p-2.5 rounded-full shadow-lg">
            <FiPhone className="w-5 h-5" />
          </a>
        </div>
      </header>

      {/* ============ HOME / HERO SECTION ============ */}
      <section id="section-home" className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-red-700">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/20 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
          <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          
          {/* Curved Shape */}
          <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 200" fill="none">
            <path d="M0 200V100C360 150 720 50 1440 100V200H0Z" fill="white" fillOpacity="0.1"/>
            <path d="M0 200V120C360 170 720 70 1440 120V200H0Z" fill="white"/>
          </svg>
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mx-auto lg:mx-0">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-sm font-medium">Adeeg Degdeg ah & Qiimmo Jaban</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="text-white">Guuritaanka </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-300 to-yellow-300">Ugu Fiican</span>
                <span className="text-white"> ee Soomaaliya</span>
              </h1>
              
              <p className="text-lg text-blue-100 max-w-md mx-auto lg:mx-0">
                Waxaan bixinaa adeegyo kala duwan oo ku saabsan guuritaanka guryaha, xafiisyada, iyo ganacsiyada. Khibrad 3+ sanno ah.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                <button 
                  type="button"
                  onClick={() => scrollToSection('section-quotation')}
                  className="bg-white text-red-600 px-8 py-4 rounded-full font-bold shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center space-x-2"
                >
                  <span>Codso Hadda</span>
                  <FiArrowRight className="w-5 h-5" />
                </button>
                <button 
                  type="button"
                  onClick={() => scrollToSection('section-services')}
                  className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full font-bold border-2 border-white/30 hover:bg-white/30 transition-all flex items-center justify-center space-x-2"
                >
                  <FiPlay className="w-5 h-5" />
                  <span>Arag Adeegyada</span>
                </button>
              </div>

              {/* Trust Badges */}
              <div className="flex items-center justify-center lg:justify-start space-x-6 pt-6">
                <div className="flex items-center space-x-2">
                  <FiShield className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-blue-100">Aamminsan</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiStar className="w-5 h-5 text-yellow-400" />
                  <span className="text-sm text-blue-100">4.9 Rating</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheck className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-blue-100">Shatiyeysan</span>
                </div>
              </div>
            </div>

            {/* Right Content - Stats Card */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 lg:p-8 border border-white/20 shadow-2xl">
                <div className="grid grid-cols-2 gap-4 lg:gap-6">
                  {stats.map((stat, index) => {
                    const IconComponent = stat.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 lg:p-6 text-center hover:bg-white/30 transition-all hover:scale-105 cursor-pointer"
                      >
                        <div className="w-12 h-12 lg:w-14 lg:h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <IconComponent className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                        </div>
                        <p className="text-2xl lg:text-4xl font-bold text-white">{stat.number}</p>
                        <p className="text-sm text-blue-200">{stat.label}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Company Badge */}
                <div className="mt-6 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-4 flex items-center space-x-4">
                  <img src="/logo.png" alt="Logo" className="h-12 w-auto bg-white rounded-full p-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white">SAHALGUUR</h3>
                    <p className="text-red-200 text-sm">SERVICES COMPANY</p>
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="hidden lg:block absolute -top-4 -right-4 w-24 h-24 bg-yellow-400 rounded-full opacity-80 animate-pulse"></div>
              <div className="hidden lg:block absolute -bottom-4 -left-4 w-16 h-16 bg-blue-400 rounded-full opacity-80 animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES SECTION ============ */}
      <section id="section-services" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-12 lg:mb-16">
            <div className="inline-flex items-center space-x-2 bg-red-100 px-4 py-2 rounded-full mb-4">
              <FiTruck className="w-4 h-4 text-red-600" />
              <span className="text-sm font-semibold text-red-600">Adeegyadayada</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Adeegyada Aan <span className="text-red-600">Bixino</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Waxaan bixinaa adeegyo kala duwan oo ku saabsan guuritaanka. Dooro adeegga ku habboon.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-3xl p-6 lg:p-8 shadow-xl border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group"
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-600">
                        <FiCheck className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button 
                    type="button"
                    onClick={() => scrollToSection('section-quotation')}
                    className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-red-600 hover:text-white transition-all flex items-center justify-center space-x-2 group-hover:bg-red-600 group-hover:text-white"
                  >
                    <span>Codso Adeeggan</span>
                    <FiArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ QUOTATION FORM SECTION ============ */}
      <section id="section-quotation" className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-red-100 px-4 py-2 rounded-full mb-4">
              <FiFileText className="w-4 h-4 text-red-600" />
              <span className="text-sm font-semibold text-red-600">Quotation Form</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Codso <span className="text-red-600">Qiimeyn</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Buuxi formka hoose si aad u hesho qiimeyn bilaash ah oo ku saabsan guuritaankaaga
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            {/* Form Header */}
            <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 px-6 py-10 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full transform translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full transform -translate-x-1/2 translate-y-1/2"></div>
              
              <div className="relative z-10">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <FiFileText className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold">Formka Quotation</h2>
                </div>
                <p className="mt-3 opacity-90 max-w-md text-lg">
                  Buuxi formka hoose si aad u codsato adeegga guuritaanka
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 lg:p-8 space-y-10">
              
              {/* Section 1: Customer Info */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white flex items-center justify-center mr-4 text-sm font-bold shadow-lg">1</span>
                  Macluumaadka Macmiilka
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Input
                    label="Magaca Oo Dhamaystiran"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    placeholder="Magacaaga qor"
                    required
                    error={errors.customerName}
                  />
                  
                  <Input
                    label="Telefoon"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="061XXXXXXX"
                    required
                    error={errors.phone}
                  />
                </div>
              </div>

              {/* Section 2: Locations */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white flex items-center justify-center mr-4 text-sm font-bold shadow-lg">2</span>
                  Goobaha
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Input
                    label="Ka gurayso (From)"
                    name="fromLocation"
                    value={formData.fromLocation}
                    onChange={handleChange}
                    placeholder="Goobta hadda"
                    required
                    error={errors.fromLocation}
                  />
                  
                  <Input
                    label="U gurayso (To)"
                    name="toLocation"
                    value={formData.toLocation}
                    onChange={handleChange}
                    placeholder="Goobta cusub"
                    required
                    error={errors.toLocation}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                  <Input
                    label="Taariikhda Guuritaanka"
                    name="movingDate"
                    type="date"
                    value={formData.movingDate}
                    onChange={handleChange}
                    required
                    error={errors.movingDate}
                  />
                  
                  <Select
                    label="Nooca Adeegga"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    options={SERVICE_TYPES}
                    required
                    error={errors.serviceType}
                  />
                </div>
              </div>

              {/* Section 3: House Type */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white flex items-center justify-center mr-4 text-sm font-bold shadow-lg">3</span>
                  Nooca Guriga
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Select
                    label="Dooro Nooca Guriga"
                    name="houseType"
                    value={formData.houseType}
                    onChange={handleChange}
                    options={HOUSE_TYPES}
                    required
                    error={errors.houseType}
                  />

                  {formData.houseType === 'dabaq' && (
                    <>
                      <Select
                        label="Biyaanada (Ka gurayso)"
                        name="floorLevel"
                        value={formData.floorLevel}
                        onChange={handleChange}
                        options={FLOOR_OPTIONS}
                        required
                        error={errors.floorLevel}
                      />
                      <Input
                        label="Magaca dabaqka / biyaaanka (ka gurayso)"
                        name="dabaqNameFrom"
                        value={formData.dabaqNameFrom}
                        onChange={handleChange}
                        placeholder="Tusaale: Hamar Tower, Biyaano 4B..."
                        required
                        error={errors.dabaqNameFrom}
                      />
                    </>
                  )}
                </div>

                {(formData.houseType === 'guri-caadi' || (formData.houseType === 'dabaq' && formData.floorLevel)) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                    <Select
                      label="Xaggee ayaad u gurayseysaa?"
                      name="destinationType"
                      value={formData.destinationType}
                      onChange={handleChange}
                      options={DESTINATION_TYPES}
                      required
                      error={errors.destinationType}
                    />

                    {formData.destinationType === 'dabaq' && (
                      <>
                        <Select
                          label="Biyaanada aad u gurayseysaa"
                          name="destinationFloor"
                          value={formData.destinationFloor}
                          onChange={handleChange}
                          options={formData.houseType === 'dabaq' 
                            ? FLOOR_OPTIONS.filter(f => parseInt(f.value) >= parseInt(formData.floorLevel))
                            : FLOOR_OPTIONS
                          }
                          required
                          error={errors.destinationFloor}
                        />
                        <Input
                          label="Magaca dabaqka / biyaaanka (u gurayso)"
                          name="dabaqNameTo"
                          value={formData.dabaqNameTo}
                          onChange={handleChange}
                          placeholder="Tusaale: Jazeera Palace, Biyaano 2A..."
                          required
                          error={errors.dabaqNameTo}
                        />
                      </>
                    )}
                  </div>
                )}

                {(formData.houseType === 'dabaq' || formData.destinationType === 'dabaq') && (
                  <div className="mt-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-4">
                      Nooca Biyaanada (Dooro nooca dabaqa jaranjar ama wiish)
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {BIYAANO_FEATURES.map((feature) => (
                        <label 
                          key={feature.value}
                          className={`
                            flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-300
                            ${formData.biyaanoFeatures?.includes(feature.value)
                              ? 'border-red-500 bg-red-50 text-red-700 shadow-lg shadow-red-100'
                              : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'
                            }
                          `}
                        >
                          <input
                            type="checkbox"
                            checked={formData.biyaanoFeatures?.includes(feature.value) || false}
                            onChange={() => handleFeatureChange(feature.value)}
                            className="w-5 h-5 text-red-600 border-gray-300 rounded-lg focus:ring-red-500"
                          />
                          <span className="ml-3 text-sm font-semibold">{feature.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {formData.houseType && (
                  <div className="mt-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <span className="font-bold">Nooca:</span>{' '}
                      {HOUSE_TYPES.find(h => h.value === formData.houseType)?.label}
                      {formData.houseType === 'dabaq' && formData.floorLevel && (
                        <>
                          {' - '}
                          <span className="font-bold">Ka gurayso:</span>{' '}
                          {FLOOR_OPTIONS.find(f => f.value === formData.floorLevel)?.label}
                          {formData.dabaqNameFrom ? ` (${formData.dabaqNameFrom})` : ''}
                        </>
                      )}
                      {formData.destinationType && (
                        <>
                          {' → '}
                          <span className="font-bold">U gurayso:</span>{' '}
                          {formData.destinationType === 'guri-caadi' 
                            ? 'Guri Caadi ah'
                            : formData.destinationFloor 
                              ? `${FLOOR_OPTIONS.find(f => f.value === formData.destinationFloor)?.label}${formData.dabaqNameTo ? ` (${formData.dabaqNameTo})` : ''}`
                              : 'Dabaq / Biyaano'
                          }
                        </>
                      )}
                    </p>
                    {formData.biyaanoFeatures?.length > 0 && (
                      <p className="text-sm text-blue-700 mt-2">
                        <span className="font-bold">Nooca Biyaanada:</span>{' '}
                        {formData.biyaanoFeatures.map(f => 
                          BIYAANO_FEATURES.find(bf => bf.value === f)?.label
                        ).join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Section 4: Items */}
              {formData.serviceType && (
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white flex items-center justify-center mr-4 text-sm font-bold shadow-lg">4</span>
                    Alaabada / Adeegyada
                  </h3>
                  
                  {formData.serviceType === 'gurista-guryaha' && (
                    <GuristaGuryahaTable
                      key="gurista-guryaha"
                      fullCatalog
                      items={formData.items}
                      onChange={handleItemsChange}
                    />
                  )}
                  
                  {formData.serviceType === 'gurista-xafiisyada' && (
                    <GuristaXafiisyadaTable
                      key="gurista-xafiisyada"
                      fullCatalog
                      items={formData.items}
                      onChange={handleItemsChange}
                    />
                  )}
                  
                  {formData.serviceType === 'gurista-ganacsiyada' && (
                    <GuristaGanacsigadaTable
                      key="gurista-ganacsiyada"
                      items={formData.items}
                      onChange={handleItemsChange}
                    />
                  )}
                </div>
              )}

              {/* Section 5: Payment */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white flex items-center justify-center mr-4 text-sm font-bold shadow-lg">5</span>
                  Nooca Lacag Bixinta
                </h3>
                
                <Select
                  label="Nooca Lacagta"
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleChange}
                  options={PAYMENT_TYPES}
                  required
                  error={errors.paymentType}
                />

                {selectedPaymentAccount && (
                  <div className="mt-5 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    {/* Provider Header with Logo */}
                    <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-4 flex items-center space-x-4">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white rounded-2xl flex items-center justify-center p-2 shadow-lg flex-shrink-0">
                        <img 
                          src={PAYMENT_LOGOS[selectedPaymentAccount.paymentType] || '/logo.png'} 
                          alt={selectedPaymentAccount.paymentType}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-white font-bold text-lg sm:text-xl truncate">{selectedPaymentAccount.paymentType}</h4>
                        <p className="text-gray-300 text-sm truncate">{selectedPaymentAccount.accountName}</p>
                      </div>
                    </div>

                    {/* Account Number with Copy */}
                    <div className="p-4 sm:p-5">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Lambarka Akoonka</p>
                      <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                        <span className="text-xl sm:text-2xl font-bold text-gray-900 tracking-wider font-mono truncate mr-3">
                          {selectedPaymentAccount.accountNumber}
                        </span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(selectedPaymentAccount.accountNumber)}
                          className={`flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl font-semibold text-sm transition-all flex-shrink-0 ${
                            copied 
                              ? 'bg-green-100 text-green-700 border border-green-200' 
                              : 'bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-200 hover:shadow-xl'
                          }`}
                        >
                          {copied ? (
                            <>
                              <FiCheck className="w-4 h-4" />
                              <span className="hidden sm:inline">Copied!</span>
                            </>
                          ) : (
                            <>
                              <FiCopy className="w-4 h-4" />
                              <span className="hidden sm:inline">Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-2 text-center">Taabo si aad u koobiyeyso lambarka</p>
                    </div>
                  </div>
                )}

                {loadingPayments && (
                  <p className="text-sm text-gray-500 mt-3">Loading...</p>
                )}
              </div>

              {/* Section 6: Signature */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white flex items-center justify-center mr-4 text-sm font-bold shadow-lg">6</span>
                  Saxiixa Macmiilka
                </h3>
                
                <SignaturePad
                  onSave={handleSignatureSave}
                  onClear={handleSignatureClear}
                  label="Saxiixaaga Gacanta"
                  required
                  error={errors.customerSignature}
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                size="lg"
                loading={submitting}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-xl shadow-red-200 text-lg py-4 rounded-xl"
              >
                Dir Codsiga
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* ============ ABOUT SECTION ============ */}
      <section id="section-about" className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Image/Card */}
            <div className="relative order-2 lg:order-1">
              <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-3xl p-8 shadow-2xl">
                <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center space-x-4 mb-6">
                    <img src="/logo.png" alt="Logo" className="h-20 w-auto bg-white rounded-full p-2" />
                    <div>
                      <h3 className="text-2xl font-bold text-white">SAHALGUUR</h3>
                      <p className="text-red-200">SERVICES COMPANY</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/20 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-white">3+</p>
                      <p className="text-sm text-red-200">Sanno Khibrad</p>
                    </div>
                    <div className="bg-white/20 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-white">100%</p>
                      <p className="text-sm text-red-200">Ku Qanacsanaan</p>
                    </div>
                    <div className="bg-white/20 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-white">50+</p>
                      <p className="text-sm text-red-200">Shaqaale</p>
                    </div>
                    <div className="bg-white/20 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-white">10+</p>
                      <p className="text-sm text-red-200">Gaari</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-yellow-400 rounded-full opacity-80 hidden lg:block"></div>
              <div className="absolute -bottom-4 -left-4 w-14 h-14 bg-blue-400 rounded-full opacity-80 hidden lg:block"></div>
            </div>

            {/* Right - Content */}
            <div className="space-y-6 order-1 lg:order-2">
              <div className="inline-flex items-center space-x-2 bg-red-100 px-4 py-2 rounded-full">
                <FiInfo className="w-4 h-4 text-red-600" />
                <span className="text-sm font-semibold text-red-600">Nagu Saabsan</span>
              </div>
              
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Shirkada Guuritaanka <span className="text-red-600">Ugu Fiican</span>
              </h2>
              
              <p className="text-gray-600 text-lg leading-relaxed">
                SAHALGUUR SERVICES COMPANY waa shirkad la aasaasay sannadkii 2020, waxaana ujeedkeedu yahay in ay bixiso adeegyo guuritaan oo tayo sare leh oo qiimo jaban.
              </p>
              
              <ul className="space-y-4">
                {[
                  'Shaqaale xirfad leh oo tababaran',
                  'Gaariyo casri ah oo ku habboon guuritaanka',
                  'Qiimo macquul ah oo aan lagu xigin',
                  'Adeeg degdeg ah 24/7',
                  'Caymis buuxa oo alaabta daboolaya'
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <FiCheck className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <button 
                type="button"
                onClick={() => scrollToSection('section-contact')}
                className="bg-red-600 text-white px-8 py-4 rounded-full font-bold shadow-xl hover:shadow-2xl hover:bg-red-700 transition-all flex items-center space-x-2"
              >
                <span>Nala Soo Xiriir</span>
                <FiArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CONTACT SECTION ============ */}
      <section id="section-contact" className="py-16 lg:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full mb-4">
              <FiPhone className="w-4 h-4 text-red-400" />
              <span className="text-sm font-semibold text-red-400">Nagala Soo Xiriir</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">
              Nagala <span className="text-red-400">Soo Xiriir</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg">
              Haddii aad qabtid su'aal ama aad rabto macluumaad dheeraad ah, fadlan nagala soo xiriir.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              {/* Phone Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all group">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                    <FiPhone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Telefoon</h3>
                    <a href="tel:+252614262600" className="text-2xl font-bold text-red-400 hover:text-red-300 transition-colors">
                      +25261 4262600
                    </a>
                    <p className="text-gray-400 text-sm mt-1">24/7 Available</p>
                  </div>
                </div>
              </div>

              {/* Email Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all group">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                    <FiMail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Email</h3>
                    <a href="mailto:info@sahalguur.com" className="text-xl font-bold text-blue-400 hover:text-blue-300 transition-colors">
                      info@sahalguur.com
                    </a>
                    <p className="text-gray-400 text-sm mt-1">Jawab degdeg ah</p>
                  </div>
                </div>
              </div>

              {/* Location Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all group">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                    <FiMapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Goobta</h3>
                    <p className="text-lg text-gray-300">Mogadishu, Somalia</p>
                    <p className="text-gray-400 text-sm mt-1">BuuloXuubey H.Wadnaha</p>
                  </div>
                </div>
              </div>

              {/* Working Hours Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all group">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform">
                    <FiClock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Wakhtiga Shaqada</h3>
                    <p className="text-lg text-gray-300">Subax - Galabkii</p>
                    <p className="text-gray-400 text-sm mt-1">Sabitda- Khamista: 7:00AM - 5:00PM</p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="pt-4">
                <p className="text-gray-400 mb-4 font-medium">Social Media</p>
                <div className="flex items-center space-x-3">
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center hover:bg-blue-500 hover:scale-110 transition-all shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center hover:opacity-90 hover:scale-110 transition-all shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-black rounded-xl flex items-center justify-center hover:bg-gray-800 hover:scale-110 transition-all border border-gray-700 shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                  <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-black rounded-xl flex items-center justify-center hover:bg-gray-800 hover:scale-110 transition-all border border-gray-700 shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
                  </a>
                  <a href="https://wa.me/252614262600" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center hover:bg-green-500 hover:scale-110 transition-all shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 lg:p-8 border border-white/10">
              <h3 className="text-2xl font-bold mb-6">Dir Fariin</h3>
              
              <form onSubmit={handleContactSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Magacaaga</label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Magacaaga qor"
                    className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@example.com"
                      className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Telefoon</label>
                    <input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="061XXXXXXX"
                      className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Farriinta</label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Farriintaada qor halkan..."
                    rows={5}
                    className="w-full px-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl hover:from-red-700 hover:to-red-800 transition-all flex items-center justify-center space-x-2"
                >
                  <FiSend className="w-5 h-5" />
                  <span>Dir Farriinta</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-gray-900 text-white pb-28 lg:pb-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <img src="/logo.png" alt="Logo" className="h-16 w-auto bg-white rounded-full p-2" />
                <div>
                  <h3 className="text-2xl font-bold text-white">SAHALGUUR</h3>
                  <p className="text-blue-400 text-sm font-medium">SERVICES COMPANY</p>
                </div>
              </div>
              <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
                Waxaan nahay shirkad hormuud ka ah adeegyada guuritaanka ee Soomaaliya. Waxaan bixinaa adeeg degdeg ah iyo qiimmo jaban.
              </p>
              <div className="flex items-center space-x-2 text-red-400 font-semibold">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                <span>Adeeg Degdeg ah & Qiimmo Jaban</span>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-6 text-white">Adeegyada</h4>
              <ul className="space-y-4">
                <li>
                  <button 
                    type="button"
                    onClick={() => scrollToSection('section-services')} 
                    className="text-gray-400 hover:text-red-400 transition-colors flex items-center space-x-2 group"
                  >
                    <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>Guurista guryaha</span>
                  </button>
                </li>
                <li>
                  <button 
                    type="button"
                    onClick={() => scrollToSection('section-services')} 
                    className="text-gray-400 hover:text-red-400 transition-colors flex items-center space-x-2 group"
                  >
                    <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>Guurista Xafiisyada</span>
                  </button>
                </li>
                <li>
                  <button 
                    type="button"
                    onClick={() => scrollToSection('section-services')} 
                    className="text-gray-400 hover:text-red-400 transition-colors flex items-center space-x-2 group"
                  >
                    <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>Guurista ganacsiyada</span>
                  </button>
                </li>
                <li>
                  <button 
                    type="button"
                    onClick={() => scrollToSection('section-quotation')} 
                    className="text-gray-400 hover:text-red-400 transition-colors flex items-center space-x-2 group"
                  >
                    <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>Codso Quotation</span>
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-bold mb-6 text-white">Nagala Soo Xariir</h4>
              <ul className="space-y-4">
                <li>
                  <a href="tel:+252614262600" className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors">
                    <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
                      <FiPhone className="w-5 h-5 text-red-400" />
                    </div>
                    <span>+25261 4262600</span>
                  </a>
                </li>
                <li>
                  <a href="mailto:info@sahalguur.com" className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors">
                    <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                      <FiMail className="w-5 h-5 text-blue-400" />
                    </div>
                    <span>info@sahalguur.com</span>
                  </a>
                </li>
                <li>
                  <div className="flex items-center space-x-3 text-gray-400">
                  <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                      <FiMapPin className="w-5 h-5 text-green-400" />
                    </div>
                    <span>Mogadishu, Somalia</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 rounded-2xl bg-white/10 border border-white/20 p-6">
            <h4 className="text-lg font-bold text-white mb-4 text-center">Ku rakib App-ka (sida native)</h4>
            <div className="bg-white rounded-xl text-gray-900">
              <PwaInstallPanel mode="client" />
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <p className="text-gray-500 text-sm text-center md:text-left">
                © 2026 SAHALGUUR SERVICES COMPANY. Dhammaan xuquuqaha waa la xafidey.
              </p>
              <div className="flex items-center space-x-4">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-all">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-pink-500 hover:to-orange-500 transition-all">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-black transition-all">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://wa.me/252614262600" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-green-600 transition-all">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* ============ MOBILE BOTTOM NAVIGATION ============ */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-gray-200 shadow-2xl z-50">
        <div className="flex items-center justify-around py-2 px-1">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollToSection(item.id)}
                className={`flex flex-col items-center py-2 px-2 rounded-2xl transition-all duration-300 min-w-[56px]
                  ${activeNav === item.id 
                    ? 'text-red-600' 
                    : 'text-gray-500'
                  }`}
              >
                <div className={`p-2 rounded-xl transition-all duration-300 ${
                  activeNav === item.id 
                    ? 'bg-red-100 shadow-lg shadow-red-100 scale-110' 
                    : 'hover:bg-gray-100'
                }`}>
                  <IconComponent className={`w-5 h-5 ${activeNav === item.id ? 'text-red-600' : 'text-gray-500'}`} />
                </div>
                <span className={`text-[10px] mt-1 font-semibold ${activeNav === item.id ? 'text-red-600' : 'text-gray-500'}`}>
                  {item.label}
                </span>
                {activeNav === item.id && (
                  <div className="w-1.5 h-1.5 bg-red-600 rounded-full mt-0.5 animate-pulse"></div>
                )}
              </button>
            );
          })}
        </div>
        {/* Safe area for iPhone */}
        <div className="h-safe-area-inset-bottom bg-white"></div>
      </nav>

      {/* ============ SCROLL TO TOP BUTTON ============ */}
      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-24 lg:bottom-8 right-4 lg:right-8 w-12 h-12 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-110 transition-all z-40 flex items-center justify-center"
          aria-label="Scroll to top"
        >
          <FiChevronUp className="w-6 h-6" />
        </button>
      )}

      {/* ============ WHATSAPP FLOATING BUTTON (Desktop only) ============ */}
      <a
        href="https://wa.me/252614262600"
        target="_blank"
        rel="noopener noreferrer"
        className="hidden lg:flex fixed bottom-8 left-8 w-14 h-14 bg-green-500 text-white rounded-full shadow-xl hover:shadow-2xl hover:scale-110 transition-all z-40 items-center justify-center group"
        aria-label="Contact on WhatsApp"
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        
        {/* Tooltip */}
        <span className="absolute left-16 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">
          WhatsApp nala soo xiriir
        </span>
      </a>

    </div>
  );
};

export default PublicQuotation;